import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useId } from "react";

export type NotifyChannel = "in_app" | "email";

/** Ban confirmation dialog — reason (required) plus optional expiry. */
export function BanDialog({
  open,
  reason,
  expiresAt,
  saving,
  onReasonChange,
  onExpiresChange,
  onClose,
  onConfirm,
}: {
  open: boolean;
  reason: string;
  expiresAt: string;
  saving: boolean;
  onReasonChange: (value: string) => void;
  onExpiresChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ban User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <TextField
            label="Reason"
            size="small"
            multiline
            minRows={4}
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
          />
          <TextField
            label="Expiry"
            type="datetime-local"
            size="small"
            value={expiresAt}
            onChange={(event) => onExpiresChange(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="button" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="small"
          color="error"
          variant="contained"
          disabled={!reason.trim() || saving}
          onClick={onConfirm}
        >
          {saving ? "Banning..." : "Confirm Ban"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Compose and send one in-app or email notification to the loaded user. */
export function NotifyDialog({
  open,
  recipientName,
  recipientEmail,
  title,
  body,
  channel,
  error,
  saving,
  onTitleChange,
  onBodyChange,
  onChannelChange,
  onClose,
  onSend,
}: {
  open: boolean;
  recipientName: string;
  recipientEmail: string | null | undefined;
  title: string;
  body: string;
  channel: NotifyChannel;
  error: string | null;
  saving: boolean;
  onTitleChange: (value: string) => void;
  onBodyChange: (value: string) => void;
  onChannelChange: (value: NotifyChannel) => void;
  onClose: () => void;
  onSend: () => void;
}) {
  const noEmailHintId = useId();
  const canSend = !saving && title.trim().length > 0 && body.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      closeAfterTransition={false}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Notify User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 1.5 }}>
            <Typography color="text.secondary" variant="overline">
              Recipient
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {recipientName}
            </Typography>
            {recipientEmail ? (
              <Typography color="text.secondary" variant="body2">
                {recipientEmail}
              </Typography>
            ) : null}
          </Box>

          <TextField
            label="Title"
            type="text"
            size="small"
            value={title}
            onChange={(event) => onTitleChange(event.target.value)}
          />
          <TextField
            label="Body"
            size="small"
            multiline
            minRows={4}
            value={body}
            onChange={(event) => onBodyChange(event.target.value)}
          />

          <FormControl component="fieldset" size="small">
            <FormLabel component="legend">Channel</FormLabel>
            <RadioGroup
              name="notify-channel"
              value={channel}
              onChange={(event) =>
                onChannelChange(event.target.value as NotifyChannel)
              }
            >
              <FormControlLabel
                value="in_app"
                control={<Radio size="small" />}
                label="In-app"
              />
              <FormControlLabel
                value="email"
                disabled={!recipientEmail}
                control={
                  <Radio
                    size="small"
                    slotProps={{
                      input: {
                        "aria-describedby": recipientEmail
                          ? undefined
                          : noEmailHintId,
                      },
                    }}
                  />
                }
                label="Email"
              />
            </RadioGroup>
            {!recipientEmail ? (
              <Typography
                id={noEmailHintId}
                color="text.secondary"
                variant="caption"
              >
                Email is unavailable — this account has no email on file.
              </Typography>
            ) : null}
          </FormControl>

          {error ? (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button type="button" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="small"
          variant="contained"
          disabled={!canSend}
          onClick={onSend}
        >
          {saving ? "Sending..." : "Send notification"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
